/**
 * Weekend / public-holiday reporting indicator.
 *
 * Phase 4 / Phase 7 bias-audit (see docs/phase-7-governance/bias-audit.md §
 * "Reporting-lag bias"): operators see a value drop on Sunday and read it
 * as "things are improving" when actually nobody reported because it's
 * Sunday. Same hazard on national holidays + multi-day breaks
 * (Christmas, Eid, Diwali, etc. depending on jurisdiction).
 *
 * This module gives downstream surfaces a `isReportingDayUnusual(date,
 * iso3)` predicate so a signal whose `asOf` falls on a weekend / holiday
 * can be visibly flagged. The threat-tier engine and the per-signal
 * detail panel both consume this — see Signal.reportingDayNote, populated
 * during fusion when the predicate fires.
 *
 * Scope: v1 ships a curated calendar for the jurisdictions with the most
 * wired adapters (AU + US + UK + DE + FR + NL + JP + CA + BR + IN + SG +
 * HK + a few more). Additions are mechanical — append the holiday list +
 * the country code mapping. NOT exhaustive; the predicate degrades to
 * "weekend-only" for jurisdictions not in the calendar.
 */

/** Per-country public holidays for the current + next year. ISO dates.
 *  Source: each country's official government holidays page; verified
 *  via the standard practice (not externally verified per the license-
 *  register pattern — this is operational data, not a legal claim, and
 *  the cost of being a day wrong is small + visible to operators). */
const PUBLIC_HOLIDAYS: Record<string, ReadonlyArray<string>> = {
  // Australia — federal public holidays (state-specific holidays NOT included
  // in this list; AU state-level holidays should be added per-state-iso when
  // a sub-national reporting calendar matters). Source: https://www.fairwork.gov.au/employment-conditions/public-holidays
  AUS: [
    '2026-01-01', '2026-01-26', '2026-04-03', '2026-04-04', '2026-04-05', '2026-04-06',
    '2026-04-25', '2026-06-08', '2026-12-25', '2026-12-26', '2026-12-28',
    '2027-01-01', '2027-01-26',
  ],
  // United States — federal holidays.
  // Source: https://www.opm.gov/policy-data-oversight/pay-leave/federal-holidays/
  USA: [
    '2026-01-01', '2026-01-19', '2026-02-16', '2026-05-25', '2026-06-19', '2026-07-03',
    '2026-09-07', '2026-10-12', '2026-11-11', '2026-11-26', '2026-12-25',
    '2027-01-01', '2027-01-18',
  ],
  // United Kingdom (England + Wales — Scotland + NI differ slightly).
  // Source: https://www.gov.uk/bank-holidays
  GBR: [
    '2026-01-01', '2026-04-03', '2026-04-06', '2026-05-04', '2026-05-25',
    '2026-08-31', '2026-12-25', '2026-12-28',
    '2027-01-01',
  ],
  // Germany — federal holidays (state Feiertage NOT included).
  // Source: https://www.bundesregierung.de/feiertage
  DEU: [
    '2026-01-01', '2026-04-03', '2026-04-06', '2026-05-01', '2026-05-14',
    '2026-05-25', '2026-10-03', '2026-12-25', '2026-12-26',
    '2027-01-01',
  ],
  // France — jours fériés.
  // Source: https://www.service-public.fr/particuliers/vosdroits/F2405
  FRA: [
    '2026-01-01', '2026-04-06', '2026-05-01', '2026-05-08', '2026-05-14',
    '2026-05-25', '2026-07-14', '2026-08-15', '2026-11-01', '2026-11-11',
    '2026-12-25', '2027-01-01',
  ],
  // Netherlands — wettelijke feestdagen.
  // Source: https://www.rijksoverheid.nl/onderwerpen/feestdagen
  NLD: [
    '2026-01-01', '2026-04-05', '2026-04-06', '2026-04-27', '2026-05-05',
    '2026-05-14', '2026-05-25', '2026-12-25', '2026-12-26',
    '2027-01-01',
  ],
  // Japan — national holidays.
  // Source: https://www.cao.go.jp/chosei/shukujitsu/gaiyou.html
  JPN: [
    '2026-01-01', '2026-01-12', '2026-02-11', '2026-02-23', '2026-03-20',
    '2026-04-29', '2026-05-03', '2026-05-04', '2026-05-05', '2026-05-06',
    '2026-07-20', '2026-08-11', '2026-09-21', '2026-09-22', '2026-10-12',
    '2026-11-03', '2026-11-23', '2027-01-01', '2027-01-11',
  ],
  // Canada — federal statutory holidays (province-specific NOT included).
  // Source: https://www.canada.ca/en/revenue-agency/services/tax/public-holidays.html
  CAN: [
    '2026-01-01', '2026-04-03', '2026-05-18', '2026-07-01', '2026-09-07',
    '2026-09-30', '2026-10-12', '2026-11-11', '2026-12-25', '2026-12-28',
    '2027-01-01',
  ],
  // Brazil — feriados nacionais.
  // Source: https://www.gov.br/secom/pt-br/acesso-a-informacao/comunicabr/lista-de-feriados-nacionais-2026
  BRA: [
    '2026-01-01', '2026-02-16', '2026-02-17', '2026-04-03', '2026-04-21',
    '2026-05-01', '2026-06-04', '2026-09-07', '2026-10-12', '2026-11-02',
    '2026-11-15', '2026-12-25', '2027-01-01',
  ],
  // India — gazetted national holidays. State holidays NOT included.
  // Source: https://www.india.gov.in/calendar
  IND: [
    '2026-01-26', '2026-03-17', '2026-04-03', '2026-08-15', '2026-10-02',
    '2026-10-20', '2026-11-08', '2026-12-25',
  ],
  // Hong Kong SAR — public holidays.
  // Source: https://www.gov.hk/en/about/abouthk/holiday/2026.htm
  HKG: [
    '2026-01-01', '2026-02-17', '2026-02-18', '2026-02-19', '2026-04-03',
    '2026-04-04', '2026-04-06', '2026-05-01', '2026-05-25', '2026-06-19',
    '2026-07-01', '2026-10-01', '2026-10-19', '2026-12-25', '2026-12-26',
  ],
  // Singapore — public holidays.
  // Source: https://www.mom.gov.sg/employment-practices/public-holidays
  SGP: [
    '2026-01-01', '2026-02-17', '2026-02-18', '2026-04-03', '2026-05-01',
    '2026-05-21', '2026-06-01', '2026-08-09', '2026-08-26', '2026-11-08',
    '2026-12-25', '2027-01-01',
  ],
  // South Africa — public holidays.
  // Source: https://www.gov.za/about-sa/public-holidays
  ZAF: [
    '2026-01-01', '2026-03-21', '2026-04-03', '2026-04-06', '2026-04-27',
    '2026-05-01', '2026-06-16', '2026-08-09', '2026-09-24', '2026-12-16',
    '2026-12-25', '2026-12-26',
  ],
};

/** Saturdays + Sundays universally — sub-Saharan-Africa + some Middle East
 *  jurisdictions weight Friday + Saturday instead, but for the broad-strokes
 *  reporting-bias flag the Sun/Sat default is right for ~90% of wired sources.
 *  Override per-jurisdiction in WEEKEND_DAYS below. */
const DEFAULT_WEEKEND_DAYS: ReadonlySet<number> = new Set([0, 6]); // Sun, Sat
const WEEKEND_DAYS: Record<string, ReadonlySet<number>> = {
  // Israel + several Middle East jurisdictions: Fri + Sat weekend.
  ISR: new Set([5, 6]),
  SAU: new Set([5, 6]),
  ARE: new Set([5, 6]),
  KWT: new Set([5, 6]),
  QAT: new Set([5, 6]),
  // Iran: Thu + Fri (4, 5) — closer to historic but workweek now includes Sat.
  IRN: new Set([4, 5]),
};

/** True when `asOf` falls on a weekend OR a curated public holiday in
 *  the named jurisdiction. Degrades to weekend-only when iso3 isn't in
 *  the curated calendar. */
export function isReportingDayUnusual(asOfIso: string, iso3?: string): boolean {
  const d = parseIso(asOfIso);
  if (!d) return false;
  const weekendDays = iso3 ? (WEEKEND_DAYS[iso3] ?? DEFAULT_WEEKEND_DAYS) : DEFAULT_WEEKEND_DAYS;
  if (weekendDays.has(d.getUTCDay())) return true;
  if (!iso3) return false;
  const holidays = PUBLIC_HOLIDAYS[iso3];
  if (!holidays) return false;
  return holidays.includes(asOfIso.slice(0, 10));
}

/** Human-readable reason string for the unusual day, or null if the day
 *  is normal. Used in tooltips + signal-detail panels. */
export function reportingDayNote(asOfIso: string, iso3?: string): string | null {
  const d = parseIso(asOfIso);
  if (!d) return null;
  const weekendDays = iso3 ? (WEEKEND_DAYS[iso3] ?? DEFAULT_WEEKEND_DAYS) : DEFAULT_WEEKEND_DAYS;
  if (weekendDays.has(d.getUTCDay())) {
    const dayName = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getUTCDay()];
    return `${dayName} reporting — value may reflect a weekend reporting gap rather than a real-world change`;
  }
  if (iso3) {
    const holidays = PUBLIC_HOLIDAYS[iso3];
    if (holidays?.includes(asOfIso.slice(0, 10))) {
      return `Public holiday in ${iso3} — value may reflect a holiday reporting gap rather than a real-world change`;
    }
  }
  return null;
}

function parseIso(iso: string): Date | null {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Exposed for tests + the eventual UI legend ("which jurisdictions does
 *  PaCC currently flag holidays for?"). */
export const SUPPORTED_HOLIDAY_JURISDICTIONS: ReadonlyArray<string> = Object.keys(PUBLIC_HOLIDAYS).sort();
